/**
 * The paths the SHELL draws itself, with no module mounted underneath.
 *
 * 🔴 **THESE ARE NOT MODULE PREFIXES, AND `boot.ts` HAS TO BE TOLD** (PNK-145). Boot
 * lands a signed-in visitor on the home route whenever the path they arrived on is not
 * under a module prefix:
 *
 * ```ts
 * const insideAModule = APP_MODULES.some((m) => !m.overlay && routeUnderPrefix(path, m.pathPrefix));
 * if (!insideAModule) navigateToUrl(APP_HOME_PATH);
 * ```
 *
 * That test is right about an unknown path and wrong about these two, because "no module
 * is mounted here" is exactly what they are. Both were bulldozed to `/productions`:
 *
 *   · `/security` — reload or bookmark "How you sign in" and you were bounced away.
 *   · `/r/<token>` — 🔴 the WORSE one. A person who is still signed in somewhere asks for a
 *     password reset, clicks the link in the mail, and the shell throws the token away and
 *     shows them the slate. Nothing fails, nothing is logged, and the reset simply does not
 *     happen — which is the one situation the mail exists to serve, since somebody who
 *     remembered their password would not have asked. Signed OUT it worked, because modules
 *     never start without a session and the redirect never ran; that is why it shipped.
 *
 * `publicPaths.ts` answers a different question — whether a MODULE the app mounts sits on a
 * prefix served signed-out — so it cannot absorb this one. These paths have no module by
 * design, and the shell is what serves them.
 */

/** The shell's own account-security screen (PNK-137) — password, passkeys, connected accounts. */
export const SECURITY_ROUTE = "/security";

/** Where a password-reset link lands: `/r/<token>`, the URL `services/auth/reset-link.ts` mints. */
const RESET_PATH = /^\/r\/([A-Za-z0-9_-]+)\/?$/u;

/** The token out of `/r/<token>`, or `null` when this is not a reset link. */
export function resetTokenOf(pathname: string): string | null {
  const found = RESET_PATH.exec(pathname);
  return found === null ? null : (found[1] as string);
}

/**
 * Whether the shell itself renders this path. `boot.ts` leaves these alone: no home
 * redirect, because there is nowhere better to send someone who arrived on one.
 */
/**
 * `/i/<token>` — where an invitation mail lands (D126). Two token shapes, and nothing else: the membership
 * edge's own `inviteHash` (a v4 UUID — hex and hyphens) and, since D159e/D161, an engagement onboarding's
 * `userInvitations` token (32 random bytes, base64url — exactly 43 characters). The server decides which.
 */
const INVITATION_PATH = /^\/i\/([A-Fa-f0-9-]{36}|[A-Za-z0-9_-]{43})\/?$/u;

export function invitationTokenOf(pathname: string): string | null {
  const found = INVITATION_PATH.exec(pathname);
  return found === null ? null : (found[1] as string);
}

/**
 * `/onboard/<token>` — 🔴 PNK-253: an onboarding START link, shown on the Desk as a QR, copied, or sent on WhatsApp.
 * It grants nothing: the page asks Pinksy to email the person their own onboarding link. The token is an `@pinksy/link`
 * `onboard` token — base64url, 159 characters today; the bounds leave room for the format to grow, never for a path.
 */
const ONBOARD_START_PATH = /^\/onboard\/([A-Za-z0-9_-]{100,400})\/?$/u;

export function onboardStartTokenOf(pathname: string): string | null {
  const found = ONBOARD_START_PATH.exec(pathname);
  return found === null ? null : (found[1] as string);
}

/**
 * 🔴 **THE TWO PAGES AN MCP CLIENT'S SIGN-IN PASSES THROUGH** (D133). The authorization server sends the
 * browser here with a signed query: first to sign in (Google, passkey, password — the Desk's own
 * sign-in), then to allow the client. Shell chrome, like the sign-in screen: never a module.
 */
export const OAUTH_AUTHORIZE_ROUTE = "/oauth/authorize";
export const OAUTH_CONSENT_ROUTE = "/oauth/consent";

export function isShellOwnedPath(pathname: string): boolean {
  return pathname === SECURITY_ROUTE || pathname === OAUTH_AUTHORIZE_ROUTE || pathname === OAUTH_CONSENT_ROUTE || resetTokenOf(pathname) !== null || invitationTokenOf(pathname) !== null || onboardStartTokenOf(pathname) !== null;
}
